import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { isEmpty } from 'lodash-es';
import {
  AutoSizer as _AutoSizer,
  CellMeasurer as _CellMeasurer,
  Grid as _Grid,
  WindowScroller as _WindowScroller,
  GridCellProps,
  WindowScrollerProps,
  AutoSizerProps,
  GridProps,
  CellMeasurerProps,
  InfiniteLoader as _InfiniteLoader,
  InfiniteLoaderProps,
  GridCellRenderer,
  Index,
  Size,
  CellMeasurerCache,
} from 'react-virtualized';

const WindowScroller = _WindowScroller as unknown as FC<WindowScrollerProps>;
const AutoSizer = _AutoSizer as unknown as FC<AutoSizerProps>;
const Grid = _Grid as unknown as FC<GridProps>;
const CellMeasurer = _CellMeasurer as unknown as FC<CellMeasurerProps>;
const InfiniteLoader = _InfiniteLoader as unknown as FC<InfiniteLoaderProps>;

export interface GridInfiniteScrollerPropsType<ITEM extends {}>
  extends Pick<WindowScrollerProps, 'onScroll'> {
  /**
   * isElement: 엘리먼트 스크롤 사용 여부
   * default: false(윈도우 스크롤)
   */
  isElement?: boolean;
  /** isElement 사용시 containerRef 정보 받을수 있는 함수 */
  elementContainerRef?: (ref?: Element) => void;
  columnCount?: number;
  columnSpace?: number;
  rowSpace?: number;
  list: ITEM[];
  children: (
    args: GridCellProps & {
      item: ITEM;
    },
  ) => React.ReactNode;
  handleNextMore?: () => void;
  /**
   * 캐시 갱신해야 하는경우 관련 state dependency array 정의
   */
  cacheClearEffectDeps?: any[];
}

export const cache = new CellMeasurerCache({
  fixedWidth: true,
  defaultHeight: 30,
});

/**
 * Grid 무한스크롤 컴포넌트(react-virtualized)
 * @author 이현우
 * @param param0
 * @returns
 */
function GridInfiniteScroller<ITEM extends {}>({
  isElement,
  elementContainerRef,
  columnCount = 5,
  columnSpace = 21,
  rowSpace = 21,
  list,
  children,
  handleNextMore = () => {},
  onScroll,
  cacheClearEffectDeps = [],
}: GridInfiniteScrollerPropsType<ITEM>) {
  const [rowCount, setRowCount] = useState(0);
  const gridRef = useRef<_Grid | undefined>(undefined);
  const [columnWidth, setColumnWidth] = useState<number>(100);
  const [layoutWidth, setLayoutWidth] = useState<number>(750);
  const [isLoading, setIsLoading] = useState(false);
  const [containerRef, setContainerRef] = useState<Element>();

  const isRowLoaded = useCallback(
    ({ index }: Index) => {
      return index < rowCount - 1;
    },
    [rowCount],
  );

  const loadMoreRows = useCallback(async () => {
    if (!isLoading) {
      setIsLoading(true);
      handleNextMore();
      setIsLoading(false);
    }
  }, [handleNextMore, isLoading]);

  /**
   * 리사이즈 이벤트
   */
  const handleResize = useCallback(
    ({ width }: Size) => {
      setColumnWidth(width / columnCount);
      setLayoutWidth(width);
      cache.clearAll();
      gridRef.current?.recomputeGridSize();
    },
    [columnCount],
  );

  const cellRenderer = useCallback<GridCellRenderer>(
    (gridCellProps) => {
      const { key, rowIndex, columnIndex, style, parent } = gridCellProps;
      const cursor = rowIndex * columnCount + columnIndex;

      const width =
        (layoutWidth - (columnCount - 1) * columnSpace) / columnCount;
      const leftPadding = columnIndex * columnSpace;
      const left = width * columnIndex + leftPadding;

      const customStyle: React.CSSProperties = {
        ...style,
        width,
        left,
        paddingBottom: rowSpace,
        boxSizing: 'border-box',
      };

      return (
        <CellMeasurer
          key={key}
          cache={cache}
          parent={parent}
          columnIndex={columnIndex}
          rowIndex={rowIndex}
        >
          <div key={key} style={customStyle}>
            {cursor < list.length && (
              <>{children({ ...gridCellProps, item: list[cursor] })}</>
            )}
          </div>
        </CellMeasurer>
      );
    },
    [children, columnCount, columnSpace, layoutWidth, list, rowSpace],
  );

  const cacheClearEffectFunc = useCallback(() => {
    if (cacheClearEffectDeps?.length > 0) {
      setTimeout(() => {
        cache.clearAll();
      }, 0);
    }
  }, [cacheClearEffectDeps?.length]);

  useEffect(() => {
    if (list.length > 0) {
      setRowCount(Math.ceil(list.length / columnCount));
    }
  }, [columnCount, list]);

  useEffect(() => {
    cacheClearEffectFunc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...cacheClearEffectDeps]);

  if (isEmpty(list)) return <></>;

  const content = (
    <InfiniteLoader
      isRowLoaded={isRowLoaded}
      loadMoreRows={loadMoreRows}
      rowCount={rowCount}
      threshold={1}
    >
      {({ onRowsRendered, registerChild }) => {
        return (
          <WindowScroller scrollElement={containerRef} onScroll={onScroll}>
            {({ height, scrollTop }) => (
              <AutoSizer height={height} disableHeight onResize={handleResize}>
                {({ width }) => {
                  return (
                    <Grid
                      autoHeight
                      width={width}
                      height={height}
                      scrollTop={scrollTop}
                      ref={(grid: _Grid) => {
                        gridRef.current = grid as _Grid;
                        registerChild(grid);
                      }}
                      columnWidth={columnWidth}
                      columnCount={columnCount}
                      rowCount={rowCount}
                      rowHeight={cache.rowHeight}
                      cellRenderer={cellRenderer}
                      onSectionRendered={({ rowStartIndex, rowStopIndex }) => {
                        onRowsRendered({
                          startIndex: rowStartIndex,
                          stopIndex: rowStopIndex,
                        });
                      }}
                      style={{
                        outline: 'none',
                      }}
                    />
                  );
                }}
              </AutoSizer>
            )}
          </WindowScroller>
        );
      }}
    </InfiniteLoader>
  );

  return isElement ? (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        ref={(ref) => {
          setContainerRef(ref as Element);
          elementContainerRef?.(ref as Element);
        }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflowX: 'hidden',
          overflowY: 'auto',
        }}
      >
        {content}
      </div>
    </div>
  ) : (
    <div
      style={{
        width: '100%',
        height: '100%',
      }}
    >
      {content}
    </div>
  );
}

export default GridInfiniteScroller;
